import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, writesText } from '../skillUsage';

export const interfaceStateChangeSkill: CodeoSkill = {
    name: SKILL_NAMES.interfaceStateChange,
    kind: 'codeo',
    description:
        'Show a mocked interface changing state — a toggle flipping, a tab indicator sliding, a ' +
        'panel expanding, placeholders resolving into content — so a product capability reads as ' +
        'behaviour rather than as a screenshot.',
    checkLooksFor:
        'markup naming a toggle, tab, skeleton, switch or panel, and a tween on colour or the text rewritten from script',
    documentUsesSkill: check(
        (script, markup) =>
            /toggle|tab|skeleton|switch|panel/i.test(markup) &&
            (/backgroundColor|\bcolor:/.test(script) || writesText(script)),
    ),
    instructions: `# Interface State Change

A simplified interface moves from one state to another: a toggle flips and something turns on, a tab indicator slides and the panel under it changes, grey placeholder bars resolve into real content. The claim is about *behaviour* — the product does this — which a still mock cannot make.

Every version follows the same rule: one element leads, the rest respond. The toggle moves first and the panel follows; the indicator slides first and the content crossfades behind it. Simultaneous change reads as a cut.

## Markup

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center p-24">
  <div id="panel" style="position: relative; width: 1000px; height: 420px; border-radius: 24px; padding: 44px; background: var(--l5-color-1, #ffffff); box-shadow: 0 40px 90px rgba(0,0,0,0.24);">
    <div class="flex items-center gap-6">
      <div id="toggle-track" style="position: relative; width: 132px; height: 68px; border-radius: 34px; background: var(--l5-color-0, #d6dbe3);">
        <div id="toggle-knob" style="position: absolute; left: 6px; top: 6px; width: 56px; height: 56px; border-radius: 50%; background: var(--l5-color-1, #ffffff); box-shadow: 0 4px 12px rgba(0,0,0,0.28);"></div>
      </div>
      <div style="font-family: var(--l5-font-0), sans-serif; font-size: 44px; font-weight: 700; color: var(--l5-color-0, #101820)">Auto-captions</div>
      <div id="state-label" style="margin-left: auto; font-family: var(--l5-font-0), sans-serif; font-size: 28px; font-weight: 600; color: var(--l5-color-0, #5b6472)">Off</div>
    </div>
    <div id="placeholder-rows" class="flex flex-col gap-5" style="margin-top: 52px">
      <div class="row-skeleton" style="height: 30px; width: 78%; border-radius: 8px; background: var(--l5-color-0, #e6eaf0)"></div>
      <div class="row-skeleton" style="height: 30px; width: 62%; border-radius: 8px; background: var(--l5-color-0, #e6eaf0)"></div>
    </div>
    <div id="resolved-rows" class="flex flex-col gap-5" style="position: absolute; left: 44px; top: 260px; font-family: var(--l5-font-0), sans-serif; font-size: 30px; font-weight: 500; color: var(--l5-color-0, #101820);">
      <div>"…and that's how we cut it to two days."</div>
      <div>"Every language, automatically."</div>
    </div>
  </div>
</div>
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const FLIP = 1.1;

gsap.set('#toggle-knob', { x: 0 });
gsap.set('#resolved-rows', { opacity: 0, y: 8 });

// GSAP cannot interpolate into a var(), so resolve the token to a literal once at
// build time. The graphic still follows the theme — the value is read from the
// live custom property — but the track now blends instead of snapping.
const ON = getComputedStyle(document.documentElement)
    .getPropertyValue('--l5-color-2').trim() || '#38bdf8';

tl.to('#toggle-knob', { x: 64, duration: 0.34, ease: 'power3.inOut' }, FLIP);
tl.to('#toggle-track', { backgroundColor: ON, duration: 0.3, ease: 'power2.out' }, FLIP + 0.06);
// The label reads the state, so it has to change with it. Target it by its id;
// the selectable-asset label is not a selector (see Pitfalls).
tl.set('#state-label', { textContent: 'On', color: ON }, FLIP + 0.18);
// The consequence follows the cause, never with it.
tl.to('.row-skeleton', { opacity: 0, duration: 0.3, ease: 'power2.in', stagger: 0.06 }, FLIP + 0.24);
tl.to('#resolved-rows', { opacity: 1, y: 0, duration: 0.44, ease: 'power3.out' }, FLIP + 0.36);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Tab indicator**: an underline sliding between two labels, with the panel content crossfading 0.1s behind it. Same lead-and-follow shape.
- **Panel expand**: tween \`height\` on the container and stagger the newly revealed rows in. Animate an explicit height, not \`auto\`, which cannot be interpolated.
- **Skeleton to content**: the version above without the toggle — placeholders fading as real content rises. Good for "it fills itself in".
- **Progress to done**: a bar filling, then the label swapping to a check — see \`${SKILL_NAMES.springSettle}\` for the landing.
- **With a cursor**: precede the flip with a click — see \`${SKILL_NAMES.simulatedCursorClick}\` — when the point is that a *person* does this.
- **Colour as state**: the track colour changing is doing as much work as the knob moving; keep both.

## Pitfalls

- **\`data-codeo-asset\` is a label, not a selector.** It exists so the user can click the thing and so the parent agent can read its copy — it is not an id and GSAP cannot target it. Animating \`#caption-state\` when the markup says \`id="state-label" data-codeo-asset="caption-state"\` matches nothing and fails silently.
- **Never tween a colour *into* a \`var()\`.** GSAP interpolates between two resolved colours; handed \`var(--l5-color-N, …)\` as an end value it can only assign it, so the track and the state label snap at the end of the tween instead of blending — which is the one thing a state change is supposed to show. Resolve the token to a literal first, as above.
- The knob is \`x\`-tweened, so it must not carry a CSS \`transform\`; its resting offset lives in \`left\`.
- Don't animate \`height: auto\`. Measure the target height and tween to a number.
- Keep the mock to a handful of large elements. A faithful UI reproduction is unreadable at video scale and reads as a screenshot.
- The response must trail the trigger by 0.15–0.3s. Simultaneous is a cut; longer than about half a second reads as lag.
- One state change per graphic. A mock that toggles, switches tabs and expands a panel is a product tour compressed into one scene.
- Interfaces are the style guidance's default trap — no scrollbars, no nav bars, no form chrome beyond the one control the point depends on.
- Don't let the panel sit motionless afterwards; the frame still needs an ambient layer or a camera move once the state has settled.`,
};
